import { Injectable, inject } from '@angular/core';
import { firstValueFrom, take } from 'rxjs';
import { ConfigService } from '../config-service/config-service.service';
import {
  HeadlessAuthFlowResult,
  HeadlessAuthProtocolContract,
  HeadlessAuthStep,
  HeadlessAuthTokenResponse,
  HeadlessAuthTransaction,
  HeadlessAuthWireStep,
  TokenSet,
} from './headless-auth-flow.models';
import {
  clearHeadlessAuthTransaction,
  consumeHeadlessAuthTransaction,
  createHeadlessAuthTransaction,
  saveHeadlessAuthTransaction,
} from './headless-auth-pkce.util';

/** Negotiated with Keycloak on the authorization request; a major-version change fails closed. */
export const HEADLESS_PROTOCOL_VERSION = '1.0';

/**
 * Capabilities this client requires. WebAuthn/passkey ceremonies are deliberately
 * out of the v1 set: nothing in this app performs `navigator.credentials.*` yet, and
 * asking for a capability we cannot honour would let a realm route us into a step we
 * can only fail.
 */
export const HEADLESS_REQUIRED_CAPABILITIES = ['authentication_method_selection', 'recovery_navigation'];

const OIDC_SCOPE = 'openid';

interface OidcDiscovery {
  issuer: string;
  token_endpoint: string;
  jwks_uri: string;
}

interface AuthEndpoints {
  /** Base every Keycloak request is built from — same-origin proxy path in local dev. */
  requestBase: string;
  /** Issuer Keycloak declares for this realm; equals `requestBase` outside local dev. */
  issuer: string;
  realm: string;
  clientId: string;
}

type JwtClaims = Record<string, unknown> & {
  iss?: string;
  sub?: string;
  aud?: string | string[];
  azp?: string;
  nonce?: string;
  exp?: number;
};

export interface HeadlessAuthStartOptions {
  loginHint?: string;
  /**
   * Defaults to `${window.location.origin}/`. Whatever is passed must be a redirect
   * URI registered on the Keycloak client, or the flow fails at the final redirect.
   */
  redirectUri?: string;
}

/**
 * Drives Keycloak's headless browser flow from inside the app, one step at a time.
 *
 * Uses `fetch` rather than `HttpClient` because the contract needs three things
 * `HttpClient` cannot express: `credentials: 'include'` for Keycloak's authentication
 * cookies, `redirect: 'follow'`, and introspection of `response.redirected` /
 * `response.url` to recognise the final authorization redirect.
 *
 * Nothing in this service logs values, tokens, continuation URLs or step payloads —
 * the guide's telemetry rules treat all of them as credential material.
 */
@Injectable({ providedIn: 'root' })
export class HeadlessAuthFlowService {
  private readonly configService = inject(ConfigService);

  private endpoints?: AuthEndpoints;
  private discovery?: OidcDiscovery;

  /**
   * The full wire step, continuation URL included. Held privately: the guide treats
   * the continuation URL as a credential, so callers get the redacted step and this
   * service is the only thing that can submit to it.
   */
  private wireStep: HeadlessAuthWireStep | null = null;
  private redirectUri?: string;
  /** Sequencing gate — Keycloak invalidates a continuation on first use, so overlapping submits corrupt the flow. */
  private inFlight: Promise<HeadlessAuthFlowResult> | null = null;
  /** Bumped by cancel()/start() so a response that arrives after either can never revive the old flow. */
  private generation = 0;

  /** The step currently awaiting input, without its continuation URL. */
  currentStep(): HeadlessAuthStep | null {
    return this.wireStep ? toPublicStep(this.wireStep) : null;
  }

  /** Discards the held step and the PKCE transaction. Safe to call at any point. */
  cancel(): void {
    this.generation++;
    this.wireStep = null;
    this.redirectUri = undefined;
    clearHeadlessAuthTransaction();
  }

  /** Starts a fresh login transaction and returns Keycloak's first step. */
  start(options: HeadlessAuthStartOptions = {}): Promise<HeadlessAuthFlowResult> {
    return this.run(async () => {
      const endpoints = await this.resolveEndpoints();
      this.generation++;
      this.wireStep = null;
      clearHeadlessAuthTransaction();

      const redirectUri = options.redirectUri ?? `${window.location.origin}/`;
      this.redirectUri = redirectUri;
      const { transaction, challenge } = await createHeadlessAuthTransaction({
        clientId: endpoints.clientId,
        redirectUri,
      });
      saveHeadlessAuthTransaction(transaction);

      const url = new URL(
        `${endpoints.requestBase}/realms/${encodeURIComponent(endpoints.realm)}/protocol/openid-connect/auth`,
      );
      const params: Record<string, string> = {
        client_id: endpoints.clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: OIDC_SCOPE,
        state: transaction.state,
        nonce: transaction.nonce,
        code_challenge: challenge,
        code_challenge_method: 'S256',
        headless: 'true',
        headless_protocol: HEADLESS_PROTOCOL_VERSION,
        headless_capabilities: HEADLESS_REQUIRED_CAPABILITIES.join(','),
      };
      if (options.loginHint) params['login_hint'] = options.loginHint;
      for (const [name, value] of Object.entries(params)) url.searchParams.set(name, value);

      return this.request(url.href, { method: 'GET' }, endpoints);
    });
  }

  /**
   * Answers the current step. `action` selects an advertised alternative
   * (`forgot_password`, `resend_code`, `restart_flow`, `cancel_recovery`,
   * `reset_credentials`, ...) and is submitted as the `action` form field, which is a
   * different thing from the step's `action` continuation URL.
   */
  submit(values: Record<string, string | string[]>, action?: string): Promise<HeadlessAuthFlowResult> {
    return this.run(async () => {
      const endpoints = await this.resolveEndpoints();
      const continuation = this.requireContinuation(endpoints);
      const body = new URLSearchParams();
      for (const [name, value] of Object.entries(values)) {
        for (const item of Array.isArray(value) ? value : [value]) body.append(name, item);
      }
      if (action) body.append('action', action);
      return this.request(
        continuation.href,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body,
        },
        endpoints,
      );
    });
  }

  /** Switches to another server-offered authentication method from `metadata.authenticationMethods`. */
  selectMethod(executionId: string): Promise<HeadlessAuthFlowResult> {
    return this.submit({ authenticationExecution: executionId });
  }

  private run(operation: () => Promise<HeadlessAuthFlowResult>): Promise<HeadlessAuthFlowResult> {
    if (this.inFlight) {
      return Promise.reject(new Error('A sign-in request is already in progress.'));
    }
    const pending = operation().finally(() => {
      if (this.inFlight === pending) this.inFlight = null;
    });
    this.inFlight = pending;
    return pending;
  }

  private requireContinuation(endpoints: AuthEndpoints): URL {
    const action = this.wireStep?.action;
    if (!action) {
      throw new Error('There is no sign-in step awaiting input. Start sign-in again.');
    }
    return this.validateAction(action, endpoints);
  }

  private async request(
    url: string,
    init: RequestInit,
    endpoints: AuthEndpoints,
  ): Promise<HeadlessAuthFlowResult> {
    const generation = this.generation;
    const response = await fetch(url, {
      ...init,
      credentials: 'include',
      headers: { ...(init.headers ?? {}), Accept: 'application/json' },
      cache: 'no-store',
      redirect: 'follow',
    });

    if (response.redirected) {
      // Deliberate deviation from the reference SPA: it navigates the browser to the
      // callback. A login widget must stay mounted, so the flow finishes in place from
      // the followed URL instead.
      return this.completeFromCallback(response.url, endpoints);
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.toLowerCase().includes('application/json')) {
      throw new Error(
        'Keycloak returned a hosted page or unsupported response instead of a headless JSON step.',
      );
    }
    const step = (await response.json()) as HeadlessAuthWireStep;
    if (!response.ok && response.status !== 401) {
      throw new Error(step?.error?.message ?? `Keycloak returned HTTP ${response.status}.`);
    }
    this.validateStep(step, endpoints);
    if (generation !== this.generation) {
      throw new Error('The sign-in flow was restarted. Start sign-in again.');
    }
    // Assigned only once the step has passed validation, so a thrown error leaves the
    // previous step intact and the caller can retry or restart.
    this.wireStep = step;
    return { kind: 'step', step: toPublicStep(step) };
  }

  private validateStep(step: HeadlessAuthWireStep, endpoints: AuthEndpoints): void {
    if (!step || typeof step.type !== 'string' || !Array.isArray(step.fields) || !Array.isArray(step.actions)) {
      throw new Error('Keycloak returned a malformed headless step.');
    }
    if (step.status === 'interaction_required' && !step.action) {
      throw new Error('Keycloak returned an interaction without a continuation URL.');
    }
    const protocol: HeadlessAuthProtocolContract | undefined = step.protocol;
    if (!protocol || typeof protocol.version !== 'string' || !Array.isArray(protocol.capabilities)) {
      throw new Error('Keycloak did not return a versioned headless protocol contract.');
    }
    if (HEADLESS_PROTOCOL_VERSION.split('.')[0] !== protocol.version.split('.')[0]) {
      throw new Error(`Keycloak returned incompatible headless protocol version ${protocol.version}.`);
    }
    const missing = HEADLESS_REQUIRED_CAPABILITIES.filter(
      (capability) => !protocol.capabilities.includes(capability),
    );
    if (missing.length) {
      throw new Error(`Keycloak is missing required headless capabilities: ${missing.join(', ')}.`);
    }
    if (step.action) this.validateAction(step.action, endpoints);
  }

  private validateAction(action: string, endpoints: AuthEndpoints): URL {
    const url = new URL(action);
    const expected = new URL(endpoints.requestBase);
    const realmPrefix = `${expected.pathname.replace(/\/$/, '')}/realms/${encodeURIComponent(
      endpoints.realm,
    )}/`;
    if (url.origin !== expected.origin || !url.pathname.startsWith(realmPrefix)) {
      throw new Error('The continuation URL is outside the configured Keycloak realm.');
    }
    return url;
  }

  private async completeFromCallback(
    finalUrl: string,
    endpoints: AuthEndpoints,
  ): Promise<HeadlessAuthFlowResult> {
    const destination = new URL(finalUrl);
    const callback = new URL(this.redirectUri ?? `${window.location.origin}/`);
    if (destination.origin !== callback.origin || destination.pathname !== callback.pathname) {
      throw new Error('Keycloak redirected outside the registered callback. Restart sign-in.');
    }
    const params = destination.searchParams;
    const error = params.get('error');
    const state = params.get('state') ?? '';
    if (error) {
      clearHeadlessAuthTransaction();
      throw new Error(`Keycloak ended the authorization request: ${error}.`);
    }
    const code = params.get('code');
    if (!code || !state) {
      throw new Error('The authorization callback did not include code and state.');
    }
    const transaction = consumeHeadlessAuthTransaction(state);
    if (transaction.redirectUri !== callback.href) {
      throw new Error('The callback does not belong to this login transaction. Start sign-in again.');
    }
    const tokens = await this.exchangeCode(code, transaction, endpoints);
    this.wireStep = null;
    this.redirectUri = undefined;
    return { kind: 'tokens', tokens };
  }

  private async exchangeCode(
    code: string,
    transaction: HeadlessAuthTransaction,
    endpoints: AuthEndpoints,
  ): Promise<TokenSet> {
    const discovery = await this.discover(endpoints);
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: transaction.clientId,
      redirect_uri: transaction.redirectUri,
      code,
      code_verifier: transaction.codeVerifier,
    });
    const response = await fetch(discovery.token_endpoint, {
      method: 'POST',
      // The client is public: no credentials, no cookies on the token endpoint.
      credentials: 'omit',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body,
      cache: 'no-store',
    });
    if (!response.ok) {
      throw new Error(`The authorization code exchange returned HTTP ${response.status}.`);
    }
    const wire = (await response.json()) as HeadlessAuthTokenResponse;
    if (!wire.id_token) {
      throw new Error('The OpenID Connect token response did not include an ID token.');
    }
    await this.verifyIdToken(wire.id_token, transaction, discovery);
    return {
      accessToken: wire.access_token,
      refreshToken: wire.refresh_token,
      idToken: wire.id_token,
      expiresIn: wire.expires_in,
      scope: wire.scope,
    };
  }

  private async discover(endpoints: AuthEndpoints): Promise<OidcDiscovery> {
    if (this.discovery) return this.discovery;
    const response = await fetch(
      `${endpoints.requestBase}/realms/${encodeURIComponent(
        endpoints.realm,
      )}/.well-known/openid-configuration`,
      { headers: { Accept: 'application/json' }, cache: 'no-store' },
    );
    if (!response.ok) throw new Error('OpenID Provider discovery failed.');
    const discovery = (await response.json()) as OidcDiscovery;
    if (discovery.issuer !== endpoints.issuer) {
      throw new Error('The discovered issuer does not match the configured realm.');
    }
    const issuerOrigin = new URL(discovery.issuer).origin;
    for (const endpoint of [discovery.token_endpoint, discovery.jwks_uri]) {
      if (new URL(endpoint).origin !== issuerOrigin) {
        throw new Error('OpenID Provider discovery returned a cross-origin endpoint.');
      }
    }
    this.discovery = discovery;
    return discovery;
  }

  private async verifyIdToken(
    token: string,
    transaction: HeadlessAuthTransaction,
    discovery: OidcDiscovery,
  ): Promise<JwtClaims> {
    const decoded = decodeJwt(token);
    const header = decoded.header as { alg?: string; kid?: string };
    if (header.alg !== 'RS256' || !header.kid) {
      throw new Error('Only RS256 ID tokens with a key ID are accepted.');
    }
    const response = await fetch(discovery.jwks_uri, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('The realm signing keys could not be loaded.');
    const { keys } = (await response.json()) as { keys: Array<JsonWebKey & { kid?: string }> };
    const jwk = keys?.find((key) => key.kid === header.kid && key.kty === 'RSA' && key.use === 'sig');
    if (!jwk) throw new Error('The ID token signing key was not found.');
    const key = await crypto.subtle.importKey(
      'jwk',
      jwk,
      { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
      false,
      ['verify'],
    );
    const valid = await crypto.subtle.verify(
      'RSASSA-PKCS1-v1_5',
      key,
      fromBase64Url(decoded.signature),
      new TextEncoder().encode(decoded.signingInput),
    );
    if (!valid) throw new Error('The ID token signature was invalid.');

    const claims = decoded.claims;
    const now = Math.floor(Date.now() / 1000);
    const audience = Array.isArray(claims.aud) ? claims.aud : [claims.aud];
    if (claims.iss !== discovery.issuer || !audience.includes(transaction.clientId)) {
      throw new Error('The ID token issuer or audience was invalid.');
    }
    if (audience.length > 1 && claims.azp !== transaction.clientId) {
      throw new Error('The ID token authorized party was invalid.');
    }
    if (claims.nonce !== transaction.nonce) {
      throw new Error('The ID token nonce did not match the login transaction.');
    }
    if (!claims.exp || claims.exp <= now - 30) {
      throw new Error('The ID token has expired.');
    }
    return claims;
  }

  private async resolveEndpoints(): Promise<AuthEndpoints> {
    if (this.endpoints) return this.endpoints;
    const config = await firstValueFrom(this.configService.getConfig().pipe(take(1)));
    const configured = String(config?.api_endpoints?.['auth_service'] ?? '').replace(/\/+$/, '');
    const realm = config?.auth?.realm;
    const clientId = config?.auth?.client_id;
    if (!configured || !realm || !clientId) {
      throw new Error('The application configuration does not define a Keycloak auth service.');
    }
    // Mirrors KeycloakWrapperService.useCustomKeycloakService: with a local Keycloak the
    // dev server proxies /auth/** to :8088, and the login-actions endpoints carry no CORS
    // headers, so every headless request has to stay same-origin.
    const isLocalKeycloak = configured.includes('localhost:8088');
    const requestBase = isLocalKeycloak ? `${window.location.origin}/auth` : configured;
    this.endpoints = {
      requestBase,
      // The issuer is whatever Keycloak declares for itself, which behind the local proxy
      // is still the :8088 URL, not the proxied one.
      issuer: `${configured}/realms/${encodeURIComponent(realm)}`,
      realm,
      clientId,
    };
    return this.endpoints;
  }
}

/** Rebuilt field by field rather than spread-and-delete, so `action` can never leak by accident. */
function toPublicStep(step: HeadlessAuthWireStep): HeadlessAuthStep {
  const { status, type, expiresIn, fields, actions, metadata, error, protocol } = step;
  return { status, type, expiresIn, fields, actions, metadata, error, protocol };
}

function decodeJwt(token: string): {
  header: Record<string, unknown>;
  claims: JwtClaims;
  signature: string;
  signingInput: string;
} {
  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('The token was not a compact JWT.');
  const parse = (part: string) =>
    JSON.parse(new TextDecoder().decode(fromBase64Url(part))) as Record<string, unknown>;
  return {
    header: parse(parts[0]),
    claims: parse(parts[1]) as JwtClaims,
    signature: parts[2],
    signingInput: `${parts[0]}.${parts[1]}`,
  };
}

function fromBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const padded = value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=');
  return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
}
